import {normalize, schema} from 'normalizr';
import {fromBytes} from 'utils';
import {
    HOST_DEFAULT_TYPES,
    ZOOKEEPER,
    ZOOKEEPER_HOSTS_COUNT,
    DiskTypePriorities,
    ClusterType,
    HOST_TYPES,
} from './constants';

export function getResoursePresets(config, clusterType) {
    const {defaultZoneId} = config;
    const {defaultResources, resourcePresets} = config.hostTypes
        ? config.hostTypes.find((host) => host.type === HOST_DEFAULT_TYPES[clusterType])
        : config;

    return {defaultResources, resourcePresets, defaultZoneId};
}

export function getPresetNames(presets) {
    return Object.keys(
        presets.reduce((acc, preset) => {
            acc[preset.presetId] = '';
            return acc;
        }, {}),
    );
}

function getZookeeperDefault(hostTypes) {
    const {diskSize, diskTypeId, resourcePresetId} = hostTypes.find(
        ({type}) => type === ZOOKEEPER,
    ).defaultResources;

    return Array(ZOOKEEPER_HOSTS_COUNT).fill({
        size: diskSize,
        presetId: resourcePresetId,
        disk: diskTypeId,
    });
}

export const prepareConfig = (config, clusterType) => {
    const {resourcePresets, defaultResources, defaultZoneId} = getResoursePresets(
        config,
        clusterType,
    );
    // берем типы дисков от дефолтной для калькулятора зоны
    const filteredPresets = resourcePresets
        .filter(({decommissioning}) => !decommissioning)
        .map((preset) => ({
            ...preset,
            diskTypes:
                preset.zones
                    .find(({zoneId}) => zoneId === defaultZoneId)
                    ?.diskTypes.map((diskType) => ({
                        ...diskType,
                        id: `${preset.presetId}==${diskType.diskTypeId}`,
                    })) || [],
        }));

    const {availableGenerations, availableHostTypes} = filteredPresets.reduce(
        (acc, {generation, type}) => {
            acc.availableGenerations.add(generation);
            acc.availableHostTypes.add(type);
            return acc;
        },
        {
            availableGenerations: new Set(),
            availableHostTypes: new Set(),
        },
    );

    const disk = new schema.Entity('diskTypes');
    const preset = new schema.Entity(
        'presets',
        {diskTypes: [disk]},
        {
            idAttribute: (value) => `${value.type}==${value.presetId}`,
        },
    );

    const normalizedPresets = normalize(filteredPresets, [preset]);
    const presets = normalizedPresets.entities.presets;

    normalizedPresets.result
        .sort((a, b) => Number(presets[b].cpuFraction) - Number(presets[a].cpuFraction))
        .sort((a, b) => Number(presets[a].memoryLimit) - Number(presets[b].memoryLimit))
        .sort((a, b) => Number(presets[a].cpuLimit) - Number(presets[b].cpuLimit))
        .sort((a, b) => Number(presets[b].generation) - Number(presets[a].generation));

    Object.keys(normalizedPresets.entities.presets).forEach((presetId) => {
        normalizedPresets.entities.presets[presetId].diskTypes.sort((a, b) => {
            const typeA = normalizedPresets.entities.diskTypes[a].type;
            const typeB = normalizedPresets.entities.diskTypes[b].type;
            return (DiskTypePriorities[typeB] || 0) - (DiskTypePriorities[typeA] || 0);
        });
    });

    if (
        Array.isArray(config.availableEditions) &&
        config.availableEditions.length > 0 &&
        !defaultResources.edition
    ) {
        defaultResources.edition = config.availableEditions[0].id;
    }

    const presetNames = getPresetNames(filteredPresets);
    const result = {
        ...config,
        ...normalizedPresets.entities,
        resourcePresets: normalizedPresets.result,
        defaultResources,
        presetNames,
        availableGenerations,
        availableHostTypes,
    };

    if (clusterType === ClusterType.CLICKHOUSE) {
        result.zookeeper = getZookeeperDefault(config.hostTypes);
    }

    return result;
};

export function getMemSizeLabel(value, i18n) {
    return `${fromBytes(value, 'gb')} ${i18n('common', 'label_unit-gigabyte')}`;
}

export function checkServiceType(service, type) {
    return service.includes(type);
}

export function getPresetDefaultType(presetId) {
    const presetCode = presetId[0];
    switch (presetCode) {
        case 'm':
            return HOST_TYPES.MEMORY_OPTIMIZED;
        case 'h':
            return HOST_TYPES.HIGH_MEMORY;
        case 's':
            return HOST_TYPES.STANDARD;
        case 'b':
        default:
            return HOST_TYPES.BURSTABLE;
    }
}
